export type MeetingSound =
  | 'join'
  | 'leave'
  | 'enable'
  | 'disable'
  | 'screen_share'
  | 'screen_unshare'
  | 'hand_raise'
  | 'hand_lower'
  | 'reaction'
  | 'chat';

let meetingAudioContext: AudioContext | null = null;
const lastMeetingSoundAt: Partial<Record<MeetingSound, number>> = {};

const getMeetingAudioContext = () => {
  if (typeof window === 'undefined') return null;
  const AudioContextCtor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AudioContextCtor) return null;
  meetingAudioContext ||= new AudioContextCtor();
  return meetingAudioContext;
};

export const playMeetingSound = (sound: MeetingSound) => {
  const currentTime = Date.now();
  if (currentTime - (lastMeetingSoundAt[sound] ?? 0) < 600) return;
  lastMeetingSoundAt[sound] = currentTime;

  const audioContext = getMeetingAudioContext();
  if (!audioContext) return;

  if (audioContext.state === 'suspended') {
    void audioContext.resume();
  }

  const patterns: Record<MeetingSound, number[]> = {
    join: [523.25, 659.25, 783.99],
    leave: [659.25, 523.25, 392],
    enable: [587.33, 783.99],
    disable: [523.25, 392],
    screen_share: [880, 1174.66, 987.77],
    screen_unshare: [987.77, 783.99, 659.25],
    hand_raise: [659.25, 880, 1174.66],
    hand_lower: [880, 659.25],
    reaction: [783.99, 987.77, 1318.51],
    chat: [880, 1174.66],
  };
  const now = audioContext.currentTime + 0.02;
  const masterGain = audioContext.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(0.12, now + 0.025);
  masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.48);
  masterGain.connect(audioContext.destination);

  patterns[sound].forEach((frequency, index) => {
    const startAt = now + index * 0.09;
    const oscillator = audioContext.createOscillator();
    const noteGain = audioContext.createGain();

    oscillator.type =
      sound === 'screen_share' ||
      sound === 'screen_unshare' ||
      sound === 'hand_raise' ||
      sound === 'reaction' ||
      sound === 'chat'
        ? 'triangle'
        : 'sine';
    oscillator.frequency.setValueAtTime(frequency, startAt);
    noteGain.gain.setValueAtTime(0.0001, startAt);
    noteGain.gain.exponentialRampToValueAtTime(0.9, startAt + 0.018);
    noteGain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.14);

    oscillator.connect(noteGain);
    noteGain.connect(masterGain);
    oscillator.start(startAt);
    oscillator.stop(startAt + 0.16);
  });
};
